/**
 * Pickup
 */

// React
import React, { FunctionComponent, useEffect, useState } from "react";

// Components
import Pagination from "@material-ui/lab/Pagination";
import List from "./components/list";

import RenderFilter from "../../utils/listDaynamicFilterHookForm";
import { useForm } from "react-hook-form";

// UI
import theme from "../../theme";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import {
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  DialogTitle,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from "@material-ui/core";

// Icon
import Icon from "@mdi/react";
import {
  mdiArrowRight,
  mdiCheck,
  mdiClose,
  mdiMapMarkerOutline,
  mdiKeyOutline,
  mdiStateMachine,
  mdiPackage,
} from "@mdi/js";

// Helper
import { removeEmptyValueObject } from "../../utils/helpers";

// Type
import { Props } from "../parcels/interfaces";
import type { Filter } from "../../utils/listDaynamicFilter";
import { mdiCalendar } from "@mdi/js/commonjs/mdi";
import useQuery from "../../utils/hook/useQuery";
import ParcelsRepository from "client-sdk/src/Repository/parcels-repository";
import handleError from "../../utils/handleError";
type Inputs = {
  "id.equals": string;
};

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      height: "50vh",
      alignSelf: "center",
      justifyContent: "center",
      background: "#ccc",
    },
    pagination: {
      // marginTop: theme.spacing(1),
    },
    textArea: {
      width: "100%",
    },
    searchBox: {
      display: "flex",
      position: "relative",
      backgroundColor: theme.palette.common.white,
      borderRadius: "4px",
      padding: "10px 0",
      boxShadow: "0px 2px 4px 1px rgba(0,0,0,0.1)",
    },

    input: {
      marginLeft: theme.spacing(1),
      flex: 2,
      "@media (max-width: 768px)": {
        fontSize: "12px",
      },
    },
    iconButton: {
      padding: 10,
      marginRight: "10px",
      position: "relative",
      "&:before": {
        content: '""',
        position: "absolute",
        width: "1px",
        height: "36px",
        top: "4px",
        left: "-9px",
        backgroundColor: "#EDF1F4",
      },
    },
    filterBox: {
      position: "absolute",
      backgroundColor: "#fff",
      width: "100%",
      top: "47px",
      right: "0",
      marginTop: "30px",
      borderRadius: "0  0 4px  4px",
      padding: "16px 16px",
      boxSizing: "border-box",
      boxShadow: "0px 2px 4px 1px rgba(0,0,0,0.1)",
      zIndex: 10,
    },

    filterOpen: {
      display: "block",
    },
    filterClose: {
      display: "none",
    },
    paginationBox: {
      display: "flex",
    },
    selectSize: {
      width: "35px",
    },
    subtitle: {
      fontSize: "14px",
      fontWeight: 500,
      color: "#8A8A8A",
    },
    magnifyIcon: {
      transform: "rotate(-90deg)",
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: "center",
      position: "relative",
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      "&::before": {
        content: '""',
        position: "absolute",
        width: "100px",
        top: "50%",
        left: "50%",
        transform: "translate(-50%,-50%)",
        height: "100px",
        borderRadius: "50%",
        opacity: "0.2",
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: "16px",
      fontWeight: 500,
      textAlign: "center",
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: "center",
      position: "relative",
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      "&::before": {
        content: '""',
        position: "absolute",
        width: "100px",
        top: "50%",
        left: "50%",
        transform: "translate(-50%,-50%)",
        height: "100px",
        borderRadius: "50%",
        opacity: "0.2",
        backgroundColor: theme.palette.blue.main,
      },
    },
    addIcon: {
      textAlign: "center",
      position: "relative",
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      "&::before": {
        content: '""',
        position: "absolute",
        width: "100px",
        top: "50%",
        left: "50%",
        transform: "translate(-50%,-50%)",
        height: "100px",
        borderRadius: "50%",
        opacity: "0.2",
        backgroundColor: theme.palette.green.main,
      },
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  })
);

const Pickup: FunctionComponent<Props> = (props: Props) => {
  const { register, handleSubmit } = useForm<Inputs>();
  const [openFilterBox, setOpenFilterBox] = useState(false as boolean);

  const { filtersValues, setFiltersValues } = useQuery({
    "transportationType.equals": "RONAD",
    "parcelType.equals": "PARTNER",
    page: 0,
    size: 10,
  } as any);
  const [filters, setFilters] = useState([] as Filter[]);
  const [openConfirmDisable, setOpenConfirmDisable] = useState(
    false as boolean
  );
  const [openDialog, setOpenDialog] = useState(false as boolean);
  const [type, setType] = useState("edit" as string);
  const [openAdd, setOpenAdd] = useState(false as boolean);
  const [id, setId] = useState(-1 as number);
  const [openConfirmActive, setOpenConfirmActive] = useState(false as boolean);
  const [openSize, setOpenSize] = React.useState(false as boolean);
  const [parcels, setParcels] = useState([]);
  const [parcelCount, setParcelCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getParcels();
    setupFilters();
  }, [filtersValues]); // eslint-disable-line react-hooks/exhaustive-deps
  const classes = useStyles();
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({ ...filtersValues, page: page - 1 });
  };

  const getParcels = async () => {
    setLoading(true);
    const { page, size } = filtersValues;
    const payload = {
      ...removeEmptyValueObject(filtersValues),
      page,
      size,
    };
    try {
      const { response, count } = await ParcelsRepository.getParcels({
        payload,
      });
      setParcels(response.data);
      setParcelCount(count.data);
      setLoading(false);
    } catch (err) {
      handleError(err);
    }
  };

  const handleChangeSizePage = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setFiltersValues({ ...filtersValues, page: 0, size: event.target.value });
  };

  const handleOpen = (id: number, type: string) => {
    setType(type);
    setOpenDialog(true);
    setId(id);
  };

  const handleClose = () => {
    setOpenConfirmDisable(false);
    setOpenDialog(false);
    setOpenAdd(false);
    setOpenConfirmActive(false);
    setOpenSize(false);
  };

  const handleOpenConfirmUpdateState = (data: { id: number; body: any }) => {
    setId(data.id);
  };

  const onSubmit = (filterValues: any) => {
    setFiltersValues({
      ...removeEmptyValueObject(filterValues),
      page: 0,
      size: 10,
    });
    setOpenFilterBox(false);
  };

  const handleClickAway = (e: any) => {
    // TODO: Shet
    if (
      e.target.getAttribute("dir") !== "rtl" &&
      e.target.getAttribute("type") !== "checkbox" &&
      e.target.getAttribute("class") !== "PrivateSwitchBase-input-73" &&
      e.target.getAttribute("class") !==
        "MuiTypography-root MuiListItemText-primary MuiTypography-body1 MuiTypography-displayBlock" &&
      e.target.getAttribute("aria-hidden") !== "true"
    )
      setOpenFilterBox(false);
  };

  const handleClick = () => {
    setOpenFilterBox((prev) => !prev);
  };

  const setupFilters = () => {
    setFilters([
      {
        type: "input",
        id: "sourceWarehouseName.contains",
        label: "شرکت(مبدا)",
        icon: mdiMapMarkerOutline,
      },
      {
        type: "input",
        id: "id.equals",
        label: "شناسه محموله ",
        icon: mdiKeyOutline,
      },
      {
        type: "checkbox",
        id: "status.in",
        label: "وضعیت محموله",
        data: ["SUBMITTED", "SHIPPED", "DELIVERED", "CANCELLED"],
        translateType: "ParcelsStatus",
        icon: mdiStateMachine,
      },
      {
        type: "date-picker",
        id: "deliveryDate.greaterThanOrEqual",
        label: "تاریخ تحویل",
        icon: mdiCalendar,
      },
      {
        type: "checkbox",
        id: "courierCode.equals",
        label: "نوع جمع آوری",
        data: ["WEEKLY_ECONOMIC_PICKUP", "MONTHLY_ECONOMIC_PICKUP"],
        translateType: "ParcelsStatus",
        icon: mdiPackage,
      },
    ]);
  };

  const handleDisableUser = async () => {
    try {
      await ParcelsRepository.updateParcelStatus({
        payload: {
          id,
          body: "CANCELLED".replace(/'/g, '"'),
          pagination: filtersValues,
        },
      });
    } catch (err) {
      handleError(err.response);
    }
    setOpenConfirmDisable(false);
  };

  const handleOpenConfirmDisable = (id: number) => {
    setOpenConfirmDisable(true);
    setId(id);
  };
  const handleOpenConfirmActive = (id: number) => {
    setOpenConfirmActive(true);
    setId(id);
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={6}>
        <Grid container item xs={12} sm={6} alignItems="center">
          <Typography variant="h4" component="h2">
            {"لیست جمع آوری محموله "}
          </Typography>
        </Grid>

        <Grid item sm={6} xs={12}>
          <RenderFilter
            filters={filters}
            onFilter={(filtervalues: any) => {
              setFiltersValues({
                ...removeEmptyValueObject(filtervalues),
                "transportationType.equals": "RONAD",
                page: 0,
                size: 10,
              });
            }}
            mainFiltersCount={1}
            baseFilter={{
              type: "input",
              id: "id.equals",
              placeholder: "شناسه محموله",
            }}
            filterValues={filtersValues}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          {loading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {parcels.length > 0 ? (
                <List
                  data={parcels}
                  handleOpenConfirmDisable={handleOpenConfirmDisable}
                  handleOpenConfirmActive={handleOpenConfirmActive}
                  handleOpen={handleOpen}
                  changeStatus={handleOpenConfirmUpdateState}
                />
              ) : (
                <Typography variant="h3" component="div">
                  محموله موجود نیست
                </Typography>
              )}
            </>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {parcels.length > 0 && (
            <FormControl
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}>
              <div style={{ marginLeft: "10px", color: "#8a8a8a" }}>
                {" "}
                تعداد نمایش در هر صفحه:
              </div>
              <Select
                open={openSize}
                onClose={handleClose}
                onOpen={() => setOpenSize(true)}
                value={filtersValues.size}
                onChange={handleChangeSizePage}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          )}
        </Grid>
        <Grid xs={12} sm={6} container item direction="row-reverse">
          {parcels.length > 0 && (
            <Pagination
              className={classes.pagination}
              count={Math.ceil(parcelCount / filtersValues.size)}
              color="secondary"
              page={filtersValues.page + 1}
              onChange={handleChangePage}
            />
          )}
        </Grid>
      </Grid>

      <Dialog
        fullScreen
        open={openDialog}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title">
        <DialogTitle>
          {/* <div>{`
      گزارش موجودی انبار
        "${parcels.find(item => item.id === id)?.name}"
        `}</div> */}
          <Button aria-label="details" size="medium" onClick={handleClose}>
            <Icon path={mdiArrowRight} size={1} />
            {"بازگشت"}
          </Button>
        </DialogTitle>
        <DialogContent>
          {/* {type === 'edit' && (
            <Edit
              data={parcels.find(item => item.id === id)}
              partnerId={props.match.params.id}
              update={body => {
                props.updateParcelsDispatch({
                  body,
                  pagination: filtersValues,
                  id: props.match.params.id,
                });
                handleClose();
              }}
            />
          )} */}
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth
        open={openConfirmDisable || openConfirmActive}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title">
        <DialogTitle>
          <div
            style={{
              position: "absolute",
              left: "17px",
              zIndex: 10,
            }}>
            {handleClose ? (
              <IconButton
                aria-label="close"
                // className={classes.closeButton}
                onClick={handleClose}>
                <Icon path={mdiClose} size={1} />
              </IconButton>
            ) : null}
          </div>
          <div
            className={
              openConfirmDisable ? classes.cancelIcon : classes.editIcon
            }>
            {openConfirmDisable ? (
              <Icon path={mdiClose} size={4} color={theme.palette.error.main} />
            ) : (
              <Icon path={mdiCheck} size={4} color={theme.palette.blue.main} />
            )}
          </div>
          <div className={classes.cancelTitle}>
            {` از 
            
            لغو محموله با شناسه
          "${parcels.find((item) => item.id === id)?.id}"
          مطمئن هستید؟`}
          </div>
        </DialogTitle>
        <DialogActions>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <Button
                fullWidth
                autoFocus
                onClick={handleClose}
                variant="contained"
                color="secondary"
                size="large">
                {"خیر"}
              </Button>
            </Grid>

            <Grid item xs={4}>
              <Button
                onClick={handleDisableUser}
                fullWidth
                variant="contained"
                size="large">
                {"بله"}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Pickup;
